import React, { createContext, useContext, useEffect, useState, useCallback, useMemo } from 'react';
import { get, post } from '../lib/api';
import { useAuth } from './AuthContext';

const AppContext = createContext(null);

export const AppProvider = ({ children }) => {
  const { user, isMaster } = useAuth();
  const [allCompanies, setAllCompanies] = useState([]);
  const [projects, setProjects] = useState([]);
  const [costCenters, setCostCenters] = useState([]);
  const [categories, setCategories] = useState([]);
  const [closures, setClosures] = useState([]);
  const currentMonth = () => {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
  };
  const [competence, setCompetence] = useState(currentMonth());
  const [companyFilter, setCompanyFilter] = useState('all');
  // Modo de período: 'month' (padrão, usa `competence`) ou 'range' (usa competenceFrom/competenceTo)
  const [periodMode, setPeriodMode] = useState('month');
  const [competenceFrom, setCompetenceFrom] = useState(currentMonth());
  const [competenceTo, setCompetenceTo] = useState(currentMonth());

  // Companies visíveis ao usuário corrente
  const companies = useMemo(() => {
    if (!user) return [];
    if (isMaster) return allCompanies;
    const allowed = user.allowed_company_ids || [];
    return allCompanies.filter((c) => allowed.includes(c.id));
  }, [allCompanies, user, isMaster]);

  // Ajustar companyFilter se ele não está nas permitidas
  useEffect(() => {
    if (!user) return;
    if (isMaster) return;
    if (companyFilter === 'all') {
      // Restrito com 1 empresa só → fixa nela
      if (companies.length === 1) setCompanyFilter(companies[0].id);
      return;
    }
    const allowed = user.allowed_company_ids || [];
    if (!allowed.includes(companyFilter)) {
      setCompanyFilter(companies[0]?.id || 'all');
    }
  }, [user, isMaster, companies, companyFilter]);

  const reloadCompanies = useCallback(async () => {
    const c = await get('/companies');
    setAllCompanies(c);
    return c;
  }, []);
  const reloadProjects = useCallback(async () => setProjects(await get('/projects')), []);
  const reloadCostCenters = useCallback(async () => setCostCenters(await get('/cost-centers')), []);
  const reloadCategories = useCallback(async () => setCategories(await get('/categories')), []);
  const reloadClosures = useCallback(async () => setClosures(await get('/closures')), []);

  const loadAll = useCallback(async () => {
    const [c, p, cc, cat, cl] = await Promise.all([
      get('/companies'),
      get('/projects'),
      get('/cost-centers'),
      get('/categories'),
      get('/closures'),
    ]);
    setAllCompanies(c);
    setProjects(p);
    setCostCenters(cc);
    setCategories(cat);
    setClosures(cl);
    return c;
  }, []);

  useEffect(() => {
    if (!user) return;  // só carrega após autenticado
    let cancelled = false;
    (async () => {
      try {
        const c = await loadAll();
        if (cancelled) return;
        if (!c || c.length === 0) {
          try { await post('/seed/spreadsheet', null); } catch (e) { /* ignore */ }
          if (cancelled) return;
          await loadAll();
        }
      } catch (e) {
        console.error('Bootstrap error:', e);
      }
    })();
    return () => { cancelled = true; };
  }, [loadAll, user]);

  const reseed = async () => {
    await post('/seed/spreadsheet?replace=true', null);
    await loadAll();
  };

  const isLocked = (cid) => {
    return closures.some(
      (cl) =>
        cl.competence === competence &&
        cl.locked &&
        (cl.company_id === null || cl.company_id === cid || cid == null)
    );
  };

  return (
    <AppContext.Provider
      value={{
        companies, projects, costCenters, categories, closures,
        competence, setCompetence,
        periodMode, setPeriodMode,
        competenceFrom, setCompetenceFrom,
        competenceTo, setCompetenceTo,
        companyFilter, setCompanyFilter,
        loading: false,
        loadAll, reseed,
        reloadCompanies, reloadProjects, reloadCostCenters, reloadCategories, reloadClosures,
        isLocked,
      }}
    >
      {children}
    </AppContext.Provider>
  );
};

export const useApp = () => {
  const ctx = useContext(AppContext);
  if (!ctx) throw new Error('useApp must be used inside AppProvider');
  return ctx;
};
